// src/context/ParticipantAuthContext.js (User App)
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
// No need to import apiClient here if it handles its own auth headers via interceptor

// Helper to decode JWT (simplistic, doesn't verify signature)
const decodeJwt = (token) => {
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error("Failed to decode JWT:", error); // Changed from logger.error
        return null;
    }
};

const ParticipantAuthContext = createContext(null);

export const ParticipantAuthProvider = ({ children }) => {
    const [participantToken, setParticipantToken] = useState(localStorage.getItem('participantToken'));
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('participantToken'));
    const [loading, setLoading] = useState(true);
    
    const [participantId, setParticipantId] = useState(null);
    const [programId, setProgramId] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('participantToken');
        if (token) {
            const decoded = decodeJwt(token);
            if (decoded && decoded.sub && decoded.program_id) {
                setParticipantId(decoded.sub);
                setProgramId(decoded.program_id);
                setIsAuthenticated(true);
            } else {
                console.warn("Invalid or unexpected token structure in localStorage."); // Changed from logger.warn or similar
                localStorage.removeItem('participantToken');
                setIsAuthenticated(false);
            }
        } else {
            setIsAuthenticated(false);
        }
        setLoading(false);
    }, []);

    const login = useCallback((token) => {
        localStorage.setItem('participantToken', token);
        setParticipantToken(token);
        const decoded = decodeJwt(token);
        if (decoded && decoded.sub && decoded.program_id) {
            setParticipantId(decoded.sub);
            setProgramId(decoded.program_id);
            setIsAuthenticated(true);
            console.info(`Participant logged in: ID ${decoded.sub}, Program ID ${decoded.program_id}`); // Changed from logger.info
        } else {
            console.error("Login called with invalid token structure for participant."); // Changed from logger.error
            // Call logout directly here to avoid dependency cycle issues if logout was in dependencies
            localStorage.removeItem('participantToken');
            setParticipantToken(null);
            setIsAuthenticated(false);
            setParticipantId(null);
            setProgramId(null);
            console.info("Participant logged out due to invalid token during login."); // Changed from logger.info
        }
    }, []); // Removed logout from dependencies

    const logout = useCallback(() => {
        localStorage.removeItem('participantToken');
        setParticipantToken(null);
        setIsAuthenticated(false);
        setParticipantId(null);
        setProgramId(null);
        console.info("Participant logged out."); // Changed from logger.info
    }, []);


    useEffect(() => {
        if (participantToken && !isAuthenticated && !loading) {
            const decoded = decodeJwt(participantToken);
            if (decoded && decoded.sub && decoded.program_id) {
                 login(participantToken); 
            } else {
                logout(); 
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participantToken, isAuthenticated, loading, login, logout]); // Added login/logout back as they are stable due to useCallback

    const value = {
        participantToken,
        isAuthenticated,
        loading,
        participantId, 
        programId,     
        login,
        logout,
    };

    return (
        <ParticipantAuthContext.Provider value={value}>
            {children}
        </ParticipantAuthContext.Provider>
    );
};

export const useParticipantAuth = () => {
    const context = useContext(ParticipantAuthContext);
    if (!context) {
        throw new Error('useParticipantAuth must be used within a ParticipantAuthProvider');
    }
    return context;
};
